import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { assessmentApi } from '@/services/learningManagementApi';
import { useEntity, useMutation } from '@/hooks';
import { Button } from '@/components/ui/Button';
import { LoadingState, ErrorState } from '@/components/common';
import { Input } from '@/components/ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ArrowLeft, Save } from 'lucide-react';
import type {
  Assessment,
  AssessmentRequest,
  AssessmentType,
  AssessmentStatus,
  AssessmentDifficultyLevel,
  GenerationSource,
} from '@/types/learningManagement';

const assessmentSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200, 'Title must be less than 200 characters'),
  description: z.string().max(2000, 'Description must be less than 2000 characters').optional(),
  category: z.string().max(100).optional(),
  assessmentType: z.enum(['KNOWLEDGE_CHECK', 'SKILL_ASSESSMENT', 'QUIZ', 'EXAM', 'DIAGNOSTIC', 'PLACEMENT_TEST', 'CERTIFICATION', 'SELF_ASSESSMENT', 'PEER_ASSESSMENT', 'PROJECT_EVALUATION']),
  status: z.enum(['DRAFT', 'ACTIVE', 'INACTIVE', 'ARCHIVED', 'UNDER_REVIEW', 'COMPLETED', 'IN_PROGRESS', 'NOT_STARTED']),
  difficultyLevel: z.enum(['BEGINNER', 'INTERMEDIATE', 'ADVANCED', 'EXPERT']).optional(),
  totalQuestions: z.number().min(0).optional(),
  timeLimitMinutes: z.number().min(0).optional(),
  passingScorePercentage: z.number().min(0).max(100).optional(),
  maxAttempts: z.number().min(1).optional(),
  randomizeQuestions: z.boolean().optional(),
  showCorrectAnswers: z.boolean().optional(),
  immediateFeedback: z.boolean().optional(),
  associatedLearningPaths: z.array(z.string()).optional(),
  prerequisites: z.array(z.string()).optional(),
  tags: z.array(z.string()).optional(),
  adaptive: z.boolean().optional(),
  assessmentGenerated: z.enum(['SYSTEM_DEFINED', 'AI_GENERATED', 'USER_CUSTOMIZED']),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

type AssessmentFormData = z.infer<typeof assessmentSchema>;

const ASSESSMENT_TYPES: AssessmentType[] = ['KNOWLEDGE_CHECK', 'SKILL_ASSESSMENT', 'QUIZ', 'EXAM', 'DIAGNOSTIC', 'PLACEMENT_TEST', 'CERTIFICATION', 'SELF_ASSESSMENT', 'PEER_ASSESSMENT', 'PROJECT_EVALUATION'];
const STATUSES: AssessmentStatus[] = ['DRAFT', 'ACTIVE', 'INACTIVE', 'ARCHIVED', 'UNDER_REVIEW', 'COMPLETED', 'IN_PROGRESS', 'NOT_STARTED'];
const DIFFICULTY_LEVELS: AssessmentDifficultyLevel[] = ['BEGINNER', 'INTERMEDIATE', 'ADVANCED', 'EXPERT'];
const GENERATION_SOURCES: GenerationSource[] = ['SYSTEM_DEFINED', 'AI_GENERATED', 'USER_CUSTOMIZED'];

export const AssessmentFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;

  const form = useForm<AssessmentFormData>({
    resolver: zodResolver(assessmentSchema),
    defaultValues: {
      title: '',
      description: '',
      category: '',
      assessmentType: 'QUIZ',
      status: 'DRAFT',
      difficultyLevel: 'BEGINNER',
      totalQuestions: undefined,
      timeLimitMinutes: undefined,
      passingScorePercentage: undefined,
      maxAttempts: undefined,
      randomizeQuestions: false,
      showCorrectAnswers: false,
      immediateFeedback: false,
      associatedLearningPaths: [],
      prerequisites: [],
      tags: [],
      adaptive: false,
      assessmentGenerated: 'USER_CUSTOMIZED',
    },
  });

  // Fetch assessment data for edit mode
  const {
    data: _assessment,
    isLoading: isLoadingAssessment,
    error: assessmentError,
  } = useEntity<Assessment>({
    fetchFn: (assessmentId: string) => assessmentApi.getById(assessmentId),
    entityId: id,
    entityName: 'assessment',
    enabled: isEdit && !!id,
    onSuccess: (data) => {
      form.reset({
        title: data.title,
        description: data.description || '',
        category: data.category || '',
        assessmentType: data.assessmentType,
        status: data.status,
        difficultyLevel: data.difficultyLevel,
        totalQuestions: data.totalQuestions,
        timeLimitMinutes: data.timeLimitMinutes,
        passingScorePercentage: data.passingScorePercentage,
        maxAttempts: data.maxAttempts,
        randomizeQuestions: data.randomizeQuestions ?? false,
        showCorrectAnswers: data.showCorrectAnswers ?? false,
        immediateFeedback: data.immediateFeedback ?? false,
        associatedLearningPaths: data.associatedLearningPaths || [],
        prerequisites: data.prerequisites || [],
        tags: data.tags || [],
        adaptive: data.adaptive ?? false,
        assessmentGenerated: data.assessmentGenerated,
      });
    },
    onError: () => {
      navigate('/admin/assessments');
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: AssessmentFormData) => {
      const payload: AssessmentRequest = {
        ...data,
      };
      return await assessmentApi.create(payload);
    },
    entityName: 'assessment',
    operation: 'create',
    onSuccess: () => {
      navigate('/admin/assessments');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: AssessmentFormData) => {
      if (!id) throw new Error('Assessment ID is required');
      const payload: AssessmentRequest = {
        ...data,
      };
      return await assessmentApi.update(id, payload);
    },
    entityName: 'assessment',
    operation: 'update',
    onSuccess: () => {
      navigate('/admin/assessments');
    },
  });

  const onSubmit = async (data: AssessmentFormData) => {
    if (isEdit) {
      await updateMutation.mutateAsync(data);
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const isLoading = isLoadingAssessment;
  const mutation = isEdit ? updateMutation : createMutation;

  // In edit mode, show loading state while fetching
  if (isEdit && isLoading) {
    return <LoadingState message="Loading assessment..." />;
  }

  // Show error if there's an error (only after loading completes)
  if (isEdit && assessmentError) {
    return <ErrorState error={assessmentError} onRetry={() => window.location.reload()} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/assessments">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {isEdit ? 'Edit Assessment' : 'Create Assessment'}
            </h1>
            <p className="text-muted-foreground mt-2">
              {isEdit ? 'Update assessment information' : 'Create a new assessment'}
            </p>
          </div>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Enter the basic details for the assessment</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title *</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter assessment title" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <textarea
                        className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder="Enter assessment description"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="assessmentType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Assessment Type *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select assessment type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {ASSESSMENT_TYPES.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {STATUSES.map((status) => (
                            <SelectItem key={status} value={status}>
                              {status.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="difficultyLevel"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Difficulty Level</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select difficulty level" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {DIFFICULTY_LEVELS.map((level) => (
                            <SelectItem key={level} value={level}>
                              {level}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter category" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <FormField
                  control={form.control}
                  name="totalQuestions"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Total Questions</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          placeholder="Enter total questions"
                          {...field}
                          onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="timeLimitMinutes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Time Limit (Minutes)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          placeholder="Enter time limit"
                          {...field}
                          onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="passingScorePercentage"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Passing Score (%)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          placeholder="Enter passing score"
                          {...field}
                          onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="maxAttempts"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Max Attempts</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={1}
                          placeholder="Enter max attempts"
                          {...field}
                          onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="tags"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tags</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter tags separated by commas"
                          value={field.value?.join(', ') || ''}
                          onChange={(e) => {
                            const tags = e.target.value
                              .split(',')
                              .map((t) => t.trim())
                              .filter(Boolean);
                            field.onChange(tags);
                          }}
                        />
                      </FormControl>
                      <FormDescription>Separate multiple tags with commas</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <FormField
                  control={form.control}
                  name="randomizeQuestions"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Randomize Questions</FormLabel>
                        <FormDescription>Randomize question order</FormDescription>
                      </div>
                      <FormControl>
                        <input
                          type="checkbox"
                          checked={field.value}
                          onChange={field.onChange}
                          className="h-4 w-4"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="showCorrectAnswers"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Show Correct Answers</FormLabel>
                        <FormDescription>Display correct answers after completion</FormDescription>
                      </div>
                      <FormControl>
                        <input
                          type="checkbox"
                          checked={field.value}
                          onChange={field.onChange}
                          className="h-4 w-4"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="immediateFeedback"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Immediate Feedback</FormLabel>
                        <FormDescription>Provide feedback immediately</FormDescription>
                      </div>
                      <FormControl>
                        <input
                          type="checkbox"
                          checked={field.value}
                          onChange={field.onChange}
                          className="h-4 w-4"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="adaptive"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Adaptive Assessment</FormLabel>
                      <FormDescription>Adapt questions based on user performance</FormDescription>
                    </div>
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={field.onChange}
                        className="h-4 w-4"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="assessmentGenerated"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Generation Source *</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select generation source" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {GENERATION_SOURCES.map((source) => (
                          <SelectItem key={source} value={source}>
                            {source.replace(/_/g, ' ')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" asChild>
              <Link to="/admin/assessments">Cancel</Link>
            </Button>
            <Button type="submit" disabled={mutation.isLoading}>
              <Save className="mr-2 h-4 w-4" />
              {mutation.isLoading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default AssessmentFormPage;







